// MaturaSqrtDifferenceSquareClosedTask.js
// Zadanie maturalne zamkniete:
// (sqrt(A) - sqrt(B))^2, gdzie po uproszczeniu wynik jest liczba calkowita.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import { AlternativeExplanation } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

const LETTERS = ["A", "B", "C", "D"];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function uniqueNumbers(values, needed, avoid) {
  const out = [];
  const used = new Set([avoid]);
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (used.has(v)) continue;
    used.add(v);
    out.push(v);
    if (out.length >= needed) break;
  }
  return out;
}

function generateMay2025OriginalTask() {
  return {
    type: "MaturaSqrtDifferenceSquareClosedTask",
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_MAJ",
      taskNumber: 1,
    },
    data: {
      base: 2,
      m: 4,
      firstRadicand: 32,
      secondRadicand: 2,
      exprLatex: "\\left(\\sqrt{32}-\\sqrt{2}\\right)^2",
      correctValue: 18,
      options: [
        { label: "A", value: 16, isCorrect: false },
        { label: "B", value: 18, isCorrect: true },
        { label: "C", value: 30, isCorrect: false },
        { label: "D", value: 34, isCorrect: false },
      ],
      correctIndex: 1,
      maxPoints: 1,
    },
  };
}

export function generateTask(generateOptions = {}) {
  if (
    generateOptions.variant === "original" &&
    generateOptions.session === "2025_MAJ"
  ) {
    return generateMay2025OriginalTask();
  }

  // sqrt(base * m^2) - sqrt(base), np. sqrt(32) - sqrt(2)
  const base = pick([2, 3, 5]);
  const m = pick([2, 3, 4, 5]);

  const largeRadicand = base * m * m;
  const smallRadicand = base;

  // Dopuszczamy zamiane kolejnosci skladnikow:
  // (sqrt(32)-sqrt(2))^2 albo (sqrt(2)-sqrt(32))^2
  const swapped = Math.random() < 0.5;
  const firstRadicand = swapped ? smallRadicand : largeRadicand;
  const secondRadicand = swapped ? largeRadicand : smallRadicand;

  const exprLatex = `\\left(\\sqrt{${firstRadicand}}-\\sqrt{${secondRadicand}}\\right)^2`;

  const correctValue = (m - 1) * (m - 1) * base;

  // Priorytetowo wybieramy "maturalne" zmylki:
  // - a^2 - b^2 zamiast (a-b)^2
  // - zgubienie wyrazu mieszanego
  // - wziecie tylko a^2
  const distractorCandidates = [
    (m * m - 1) * base, // np. 32 - 2 = 30
    (m * m + 1) * base, // np. 32 + 2 = 34
    m * m * base, // np. 32
    (m + 1) * (m + 1) * base, // pomylka znaku: (a+b)^2
    (m - 1) * base, // brak podniesienia do kwadratu
    base, // zostawienie samego mniejszego skladnika
  ];

  const wrong = uniqueNumbers(distractorCandidates, 3, correctValue);

  const optionsRaw = [
    { value: correctValue, isCorrect: true },
    ...wrong.map((v) => ({ value: v, isCorrect: false })),
  ];

  const options = shuffle(optionsRaw).map((o, idx) => ({
    label: LETTERS[idx],
    value: o.value,
    isCorrect: o.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaSqrtDifferenceSquareClosedTask",
    data: {
      base,
      m,
      firstRadicand,
      secondRadicand,
      exprLatex,
      correctValue,
      options,
      correctIndex,
      maxPoints: 1,
    },
  };
}

export function renderTask({ data }) {
  const { exprLatex, options } = data;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>{"Zadanie maturalne - zamkni\u0119te."}</strong>
      </p>
      <p>{"Doko\u0144cz zdanie. Wybierz w\u0142a\u015bciw\u0105 odpowied\u017a spo\u015br\u00f3d podanych."}</p>
      <p>
        {"Liczba "} <InlineMath math={exprLatex} /> {" jest r\u00f3wna:"}
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      value: opt.value,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const correct = data.options[data.correctIndex];
  return <InlineMath math={`${correct.label}.\\; ${correct.value}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.value == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>{"Twoja odpowied\u017a: "}</span>
      <InlineMath math={`${userAnswer.label}.\\; ${userAnswer.value}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    base,
    m,
    exprLatex,
    correctValue,
    firstRadicand,
    secondRadicand,
  } = data;
  const leftRoot = `\\sqrt{${base * m * m}}`;
  const reducedCoeff = (firstRadicand < secondRadicand ? -1 : 1) * (m - 1);
  const reducedLatex =
    Math.abs(reducedCoeff) === 1 ? `${reducedCoeff < 0 ? "-" : ""}\\sqrt{${base}}` : `${reducedCoeff}\\sqrt{${base}}`;
  const firstRoot = firstRadicand === base ? `\\sqrt{${base}}` : `${m}\\sqrt{${base}}`;
  const secondRoot = secondRadicand === base ? `\\sqrt{${base}}` : `${m}\\sqrt{${base}}`;
  const radicandSum = firstRadicand + secondRadicand;
  const radicandProduct = firstRadicand * secondRadicand;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> {"Upro\u015b\u0107my pierwiastki:"}
      </div>
      <BlockMath
        math={`${leftRoot} = \\sqrt{${m * m}\\cdot ${base}} = \\sqrt{${m * m}}\\cdot\\sqrt{${base}} = ${m}\\sqrt{${base}}`}
      />

      <div>
        <strong>Krok 2.</strong> {"Podstawiamy do wyra\u017cenia:"}
      </div>
      <BlockMath math={`${exprLatex} = \\left(${firstRoot}-${secondRoot}\\right)^2 =`} />
      <BlockMath math={`= \\left(${reducedLatex}\\right)^2`} />

      <div>
        <strong>Krok 3.</strong> Podnosimy do kwadratu:
      </div>
      <BlockMath math={`\\left(${reducedLatex}\\right)^2 = ${reducedCoeff * reducedCoeff}\\cdot ${base} = ${correctValue}`} />

      <AlternativeExplanation
        solution={{
          title: "Inny sposób: od razu ze wzoru skróconego mnożenia",
          intro: "Nie musimy najpierw wyciągać czynników przed oba pierwiastki. Możemy od razu zastosować wzór na kwadrat różnicy.",
          steps: [
            {
              title: "Stosujemy wzór na kwadrat różnicy.",
              latex: "(a-b)^2=a^2-2ab+b^2",
            },
            {
              title: "Podstawiamy oba pierwiastki do wzoru.",
              latex: `${exprLatex}=${firstRadicand}-2\\sqrt{${firstRadicand}}\\cdot\\sqrt{${secondRadicand}}+${secondRadicand}`,
            },
            {
              title: "Łączymy pierwiastki i wykonujemy działania.",
              latex: `${radicandSum}-2\\sqrt{${radicandProduct}}=${radicandSum}-2\\cdot${m * base}=${correctValue}`,
            },
          ],
          note: "Oba sposoby prowadzą do tego samego wyniku.",
        }}
      />
    </div>
  );
}
